import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Cross, CheckCircle, ErrorCircle, IconButton } from "@myloc/myloc-gui";
import styles from "./Snackbar.module.scss";
import { ValueOf } from "../../../utils/dataTypes";

export const SEVERITY = { ERROR: "error", WARNING: "warning", INFO: "info", SUCCESS: "success" } as const;
export const POSITION = { TOP: "top", BOTTOM: "bottom", RIGHT: "right" } as const;

const Snackbar = ({
  onClose,
  message,
  severity = SEVERITY.ERROR,
  position = POSITION.BOTTOM,
  duration = 5000,
  autoHide = true,
}: {
  onClose: () => void;
  message?: string;
  severity?: ValueOf<typeof SEVERITY>;
  position?: ValueOf<typeof POSITION>;
  duration?: number;
  autoHide?: boolean;
}) => {
  const [visible, setVisible] = useState(false);

  const handleClose = useCallback(() => {
    setVisible(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (message) {
      setVisible(true);

      if (autoHide) {
        const timeout = setTimeout(handleClose, duration);

        return () => clearTimeout(timeout);
      }
    }
  }, [message, duration, autoHide, handleClose]);

  let alertIcon;

  switch (severity) {
    case SEVERITY.WARNING:
      alertIcon = <ErrorCircle />;
      break;
    case SEVERITY.INFO:
      alertIcon = <ErrorCircle customCssClass={styles.info} />;
      break;
    case SEVERITY.SUCCESS:
      alertIcon = <CheckCircle size="20" customCssClass={styles.check} />;
      break;
    default:
      alertIcon = <ErrorCircle />;
      break;
  }

  return (
    <>
      {visible && (
        <section className={classNames(styles.snackbar, styles[position])}>
          <article className={classNames(styles.toast, styles[severity], styles[position])}>
            <figure className={styles.icon}>{alertIcon}</figure>
            <p className={styles.message}>{message}</p>
            <IconButton onClick={handleClose} customCssClass={styles.closeBtn}>
              <Cross size="24" color="currentColor" />
            </IconButton>
          </article>
        </section>
      )}
    </>
  );
};

Snackbar.propTypes = {
  onClose: PropTypes.func.isRequired,
  message: PropTypes.string,
  severity: PropTypes.oneOf(Object.values(SEVERITY)),
  position: PropTypes.oneOf(Object.values(POSITION)),
  duration: PropTypes.number,
  autoHide: PropTypes.bool,
};

export default Snackbar;
